import AppShell from "../components/layout/AppShell";
import AppFooter from "../components/layout/AppFooter";
import TopNav from "../components/layout/TopNav";
import RemoteSession from '../components/RemoteSession';

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

export default function Dashboard({ onLogout, onNavigate }) {
    const [tickets, setTickets] = useState([]);
    const [description, setDescription] = useState('');
    const [diagnostic, setDiagnostic] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastDescription, setLastDescription] = useState('');
    const [sortOrder, setSortOrder] = useState("recent");
    const [hasCard, setHasCard] = useState(null);
    const [sortOpen, setSortOpen] = useState(false);
    const [reviews, setReviews] = useState({});
    const [reviewForm, setReviewForm] = useState({});
    const [activeSession, setActiveSession] = useState(null);
    const [expandedTicket, setExpandedTicket] = useState(null);
    const [confirmModal, setConfirmModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(null);
    const [ticketType, setTicketType] = useState('standard');
    const [endModal, setEndModal] = useState(null);
    const [validateModal, setValidateModal] = useState(null);

    // Tickets actifs (tout sauf résolu)
    const activeTickets = [...tickets]
        .filter(t => t.status !== 'resolu' && t.status !== 'annule')
        .sort((a, b) => sortOrder === "recent"
            ? new Date(b.created_at) - new Date(a.created_at)
            : new Date(a.created_at) - new Date(b.created_at)
        );

    // Tickets résolus
    const resolvedTickets = [...tickets]
        .filter(t => t.status === 'resolu')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const fetchTickets = async () => {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/tickets`, { headers });
        setTickets(res.data);
        fetchReviews(res.data);
    };

    const fetchReviews = async (ticketList) => {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const reviewMap = {};
        await Promise.all(
            ticketList
                .filter(t => t.status === 'resolu')
                .map(async (t) => {
                    try {
                        const res = await axios.get(`${API}/reviews/ticket/${t.id}`, { headers });
                        reviewMap[t.id] = res.data;
                    } catch { reviewMap[t.id] = null; }
                })
        );
        setReviews(reviewMap);
    };

    const prevTicketsRef = useRef([]);

    const submitReview = async (ticketId, signalement = false) => {
        const form = reviewForm[ticketId] || {};
        if (!form.note) return alert('Veuillez donner une note.');
        setValidateModal({ ticketId, signalement });
    };

    const confirmValidation = async () => {
        const { ticketId, signalement } = validateModal;
        const form = reviewForm[ticketId] || {};
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        try {
            await axios.post(`${API}/reviews`, {
                ticket_id: ticketId,
                note: form.note,
                commentaire: form.commentaire || '',
                signalement,
            }, { headers });
            // Valide le ticket
            await axios.patch(`${API}/tickets/${ticketId}/validate`, {}, { headers });
            setValidateModal(null);
            fetchTickets();
        } catch (err) {
            console.error(err);
        }
    };

    const checkCard = useCallback(async () => {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/payments/has-card`, { headers });
        setHasCard(res.data.has_card);
    }, []);

    useEffect(() => {
        const init = async () => {
            await fetchTickets();
            checkCard();
            // Initialise le ref pour le polling
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            try {
                const res = await axios.get(`${API}/tickets`, { headers });
                prevTicketsRef.current = res.data;
            } catch { }
        };
        init();
    }, [checkCard]); // eslint-disable-line

    useEffect(() => {
        const poll = setInterval(async () => {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            try {
                const res = await axios.get(`${API}/tickets`, { headers });
                const newTickets = res.data;

                // Détecte un ticket qui vient de passer en "resolu"
                prevTicketsRef.current.forEach(prev => {
                    if (prev.status === 'en_cours') {
                        const updated = newTickets.find(t => t.id === prev.id);
                        if (updated && updated.status === 'en_validation') {
                            // Fetch la session pour avoir les montants
                            axios.get(`${API}/sessions`, { headers }).then(sessRes => {
                                const session = sessRes.data.find(s => String(s.ticket_id) === String(prev.id) && s.status === 'terminee');
                                if (session) {
                                    setEndModal({
                                        ticketId: prev.id,
                                        duration: session.duration_minutes,
                                        total: parseFloat(session.amount_charged),
                                        type: updated.type || 'standard',
                                    });
                                }
                            });
                        }
                    }
                });

                prevTicketsRef.current = newTickets;
                setTickets(newTickets);
                fetchReviews(newTickets);
            } catch (err) {
                console.error(err);
            }
        }, 10000);
        return () => clearInterval(poll);
    }, []); // eslint-disable-line

    useEffect(() => {
        const onDown = (e) => {
            if (!e.target.closest(".sort")) setSortOpen(false);
        };
        window.addEventListener("mousedown", onDown);
        return () => window.removeEventListener("mousedown", onDown);
    }, []);

    const createTicket = async () => {
        setConfirmModal(false);
        setLoading(true);
        setDiagnostic(null);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.post(`${API}/tickets`, {
                description,
                type: ticketType
            }, { headers });
            setLastDescription(description);
            setDescription('');
            setTicketType('standard');
            fetchTickets();
            // Auto-expand le nouveau ticket pour voir le diag
            if (res.data.ticket?.id) {
                setExpandedTicket(res.data.ticket.id);
            }
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!hasCard) {
            onNavigate("payment-methods");
            return;
        }
        if (!description.trim()) return;
        setConfirmModal(true);
    };

    const cancelTicket = async (ticketId) => {
        setCancelModal(null);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            await axios.patch(`${API}/tickets/${ticketId}/cancel`, {}, { headers });
            fetchTickets();
        } catch (err) {
            console.error(err);
        }
    };

    const formatStatus = (status) => {
        switch (status) {
            case "en_attente": return "En attente";
            case "en_cours": return "En cours";
            case "resolu": return "Résolu";
            case "en_litige": return "Vérification en cours";
            case "annule": return "Annulé";
            case "en_validation": return "En attente de validation";
            default: return status;
        }
    };

    const formatDate = (value) => {
        if (!value) return "—";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "—";
        return new Intl.DateTimeFormat("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        }).format(d);
    };

    return (
        <AppShell
            header={<TopNav isLoggedIn={true} onLogout={onLogout} onNavigate={onNavigate} />}
            footer={<AppFooter onNavigate={onNavigate} />}
            onNavigate={onNavigate}
        >
            <div style={{ maxWidth: 800, margin: "0 auto" }}>
                <h2 className="page-title">Tableau de bord</h2>

                {/* Tickets actifs */}
                <div className="section-card">
                    <div className="section-head">
                        <h3 className="section-title">Tickets en cours</h3>
                        <div className="sort">
                            <button
                                type="button"
                                className="sort-btn"
                                onClick={() => setSortOpen(v => !v)}
                                aria-haspopup="listbox"
                                aria-expanded={sortOpen}
                            >
                                {sortOrder === "recent" ? "Plus récents" : "Plus anciens"}
                                <span className="sort-caret">▾</span>
                            </button>
                            {sortOpen && (
                                <div className="sort-menu" role="listbox">
                                    <button type="button" className={`sort-item ${sortOrder === "recent" ? "active" : ""}`} onClick={() => { setSortOrder("recent"); setSortOpen(false); }}>Plus récents</button>
                                    <button type="button" className={`sort-item ${sortOrder === "oldest" ? "active" : ""}`} onClick={() => { setSortOrder("oldest"); setSortOpen(false); }}>Plus anciens</button>
                                </div>
                            )}
                        </div>
                    </div>

                    {activeTickets.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">📩</div>
                            <p className="empty-title">Aucun ticket actif</p>
                            <p className="empty-sub">Décris ton problème ci-dessus pour lancer un diagnostic.</p>
                        </div>
                    ) : (
                        activeTickets.map((ticket) => (
                            <div
                                key={ticket.id}
                                className="card ticket-card"
                                onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                                style={{ cursor: "pointer" }}
                            >
                                <p className="ticket-title" title={ticket.description}>#{ticket.id} — {ticket.description}</p>
                                <div className="ticket-date">{formatDate(ticket.created_at || ticket.createdAt)}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                                    <div className={`badge ${ticket.status === "en_cours" ? "progress" : ticket.status === "en_litige" ? "warning" : ticket.status === "annule" ? "cancelled" : ticket.status === "en_validation" ? "validation" : "waiting"}`}>
                                        <span className="badge-dot" />
                                        <span>{formatStatus(ticket.status)}</span>
                                    </div>
                                    {ticket.type === 'express' && (
                                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80' }}>
                                            ⚡ EXPRESS
                                        </span>
                                    )}
                                    {ticket.status === 'en_attente' && (
                                        <button
                                            className="btn btn-danger"
                                            style={{ fontSize: 12, padding: '5px 12px' }}
                                            onClick={(e) => { e.stopPropagation(); setCancelModal(ticket.id); }}
                                        >
                                            Annuler le ticket
                                        </button>
                                    )}
                                </div>

                                {/* Session remote — au dessus du diag */}
                                <div onClick={e => e.stopPropagation()}>
                                    {ticket.status === 'en_cours' && activeSession?.ticket_id === ticket.id ? (
                                        <RemoteSession sessionId={ticket.id} role="client" onEnd={() => setActiveSession(null)} ticketType={ticket.type} />
                                    ) : ticket.status === 'en_cours' ? (
                                        <div style={{ marginTop: 12 }}>
                                            <button
                                                className="btn btn-primary"
                                                style={{ fontSize: 12, padding: '6px 14px' }}
                                                onClick={async () => {
                                                    const token = localStorage.getItem('token');
                                                    const headers = { Authorization: `Bearer ${token}` };
                                                    try {
                                                        const sessRes = await axios.get(`${API}/sessions`, { headers });
                                                        const session = sessRes.data.find(s => String(s.ticket_id) === String(ticket.id) && s.status === 'en_cours');
                                                        if (session) {
                                                            await axios.patch(`${API}/sessions/${session.id}/client-join`, {}, { headers });
                                                            setActiveSession({ id: ticket.id, ticket_id: ticket.id });
                                                        } else {
                                                            setActiveSession({ id: ticket.id, ticket_id: ticket.id });
                                                        }
                                                    } catch {
                                                        setActiveSession({ id: ticket.id, ticket_id: ticket.id });
                                                    }
                                                }}
                                            >
                                                🖥️ Rejoindre la session
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                                {/* Notation — visible quand en_validation */}
                                {ticket.status === 'en_validation' && (
                                    <div onClick={e => e.stopPropagation()} style={{ marginTop: 16, borderTop: '1px solid #2a2a2a', paddingTop: 16 }}>
                                        <p style={{ fontSize: 13, color: '#fbbf24', marginBottom: 10, fontWeight: 600 }}>⭐ Évaluez l'intervention pour valider le ticket</p>
                                        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                            {[1, 2, 3, 4, 5].map(star => (
                                                <button key={star} type="button"
                                                    onClick={() => setReviewForm(f => ({ ...f, [ticket.id]: { ...f[ticket.id], note: star } }))}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: (reviewForm[ticket.id]?.note || 0) >= star ? '#f59e0b' : '#444' }}
                                                >★</button>
                                            ))}
                                        </div>
                                        <textarea
                                            placeholder="Commentaire (optionnel)..."
                                            value={reviewForm[ticket.id]?.commentaire || ''}
                                            onChange={e => setReviewForm(f => ({ ...f, [ticket.id]: { ...f[ticket.id], commentaire: e.target.value } }))}
                                            className="textarea"
                                            style={{ minHeight: 60, marginBottom: 10 }}
                                        />
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => submitReview(ticket.id, false)}>Valider l'intervention</button>
                                            <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => submitReview(ticket.id, true)}>Signaler un problème</button>
                                        </div>
                                    </div>
                                )}
                                {/* Diag IA — en dessous de la session */}
                                {expandedTicket === ticket.id && ticket.diagnostic && (() => {
                                    try {
                                        const d = typeof ticket.diagnostic === 'string'
                                            ? JSON.parse(ticket.diagnostic)
                                            : ticket.diagnostic;
                                        return (
                                            <div className="ai-box" style={{ marginTop: 12, fontSize: 12 }}>
                                                <h4 style={{ marginTop: 0, fontSize: 13 }}>Diagnostic</h4>
                                                <p><strong>Catégorie :</strong> {d.categorie} · <strong>Urgence :</strong> {d.urgence}</p>
                                                {d.temps_estime && <p><strong>Temps estimé :</strong> {d.temps_estime}</p>}
                                                <p><strong>Hypothèses :</strong></p>
                                                <ul>{d.hypotheses?.slice(0, 3).map((h, i) => <li key={i}>{h}</li>)}</ul>
                                                <p><strong>Étapes :</strong></p>
                                                <ul>{d.etapes?.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}</ul>
                                            </div>
                                        );
                                    } catch { return null; }
                                })()}
                            </div>
                        ))
                    )}
                </div>

                {/* Nouveau ticket */}
                <div className="section-card">
                    <div className="section-head">
                        <h3 className="section-title">Nouveau ticket</h3>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <textarea
                            placeholder="Décrivez votre problème..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="textarea"
                        />

                        {/* Choix du forfait */}
                        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                            <div
                                onClick={() => setTicketType('standard')}
                                style={{
                                    flex: 1, padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                                    background: ticketType === 'standard' ? 'rgba(110,86,207,0.12)' : 'rgba(255,255,255,0.025)',
                                    border: `1px solid ${ticketType === 'standard' ? 'rgba(110,86,207,0.4)' : 'rgba(255,255,255,0.06)'}`,
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>À la demande</div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: ticketType === 'standard' ? '#a78bfa' : '#fff', marginBottom: 4 }}>20€</div>
                                <div className="muted" style={{ fontSize: 12 }}>10 min incluses + 0,75€/min</div>
                            </div>
                            <div
                                onClick={() => setTicketType('express')}
                                style={{
                                    flex: 1, padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                                    background: ticketType === 'express' ? 'rgba(74,222,128,0.10)' : 'rgba(255,255,255,0.025)',
                                    border: `1px solid ${ticketType === 'express' ? 'rgba(74,222,128,0.35)' : 'rgba(255,255,255,0.06)'}`,
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                    <span style={{ fontSize: 14, fontWeight: 700 }}>Fix Express</span>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}>PRIORITAIRE</span>
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: ticketType === 'express' ? '#4ade80' : '#fff', marginBottom: 4 }}>29€</div>
                                <div className="muted" style={{ fontSize: 12 }}>15 min max, puis 0,75€/min</div>
                            </div>
                        </div>

                        <div style={{
                            marginTop: 12,
                            padding: '12px 14px',
                            borderRadius: 12,
                            border: '1px solid rgba(110,86,207,0.2)',
                            background: 'rgba(110,86,207,0.06)',
                            fontSize: 13,
                            color: 'rgba(220,220,235,0.9)',
                            fontStyle: 'italic',
                            lineHeight: 1.6
                        }}>
                            💳 Un moyen de paiement est requis pour soumettre un ticket.{' '}
                            <button
                                type="button"
                                onClick={() => onNavigate("contact-faq-payment")}
                                style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: 13, fontStyle: 'italic', padding: 0, textDecoration: 'underline' }}
                            >
                                Pourquoi ?
                            </button>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary"
                            style={{ marginTop: 12 }}
                        >
                            {loading ? "Analyse en cours..." : "Soumettre"}
                        </button>
                    </form>
                </div>



                {/* Historique */}
                {resolvedTickets.length > 0 && (
                    <div className="section-card">
                        <div className="section-head">
                            <h3 className="section-title">Historique</h3>
                        </div>
                        {resolvedTickets.map((ticket) => (
                            <div
                                key={ticket.id}
                                className="card ticket-card"
                                onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                                style={{ cursor: "pointer" }}
                            >
                                <p className="ticket-title" title={ticket.description}>#{ticket.id} — {ticket.description}</p>
                                <div className="ticket-date">{formatDate(ticket.created_at || ticket.createdAt)}</div>
                                <div className="badge done">
                                    <span className="badge-dot" />
                                    <span>Résolu</span>
                                </div>

                                {expandedTicket === ticket.id && ticket.diagnostic && (() => {
                                    try {
                                        const d = typeof ticket.diagnostic === 'string'
                                            ? JSON.parse(ticket.diagnostic)
                                            : ticket.diagnostic;
                                        return (
                                            <div className="ai-box" style={{ marginTop: 12, fontSize: 12 }}>
                                                <h4 style={{ marginTop: 0, fontSize: 13 }}>Diagnostic</h4>
                                                <p><strong>Catégorie :</strong> {d.categorie}</p>
                                                <p><strong>Urgence :</strong> {d.urgence}</p>
                                                {d.temps_estime && <p><strong>Temps estimé :</strong> {d.temps_estime}</p>}
                                                {d.complexite && <p><strong>Complexité :</strong> {d.complexite}</p>}
                                                {d.probabilite_resolution && <p><strong>Probabilité de résolution :</strong> {d.probabilite_resolution}</p>}
                                                {d.os_detecte && <p><strong>OS détecté :</strong> {d.os_detecte}</p>}
                                                <p><strong>Hypothèses :</strong></p>
                                                <ul>{d.hypotheses?.map((h, i) => <li key={i}>{h}</li>)}</ul>
                                                <p><strong>Étapes :</strong></p>
                                                <ul>{d.etapes?.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                            </div>
                                        );
                                    } catch { return null; }
                                })()}

                                <div onClick={e => e.stopPropagation()}>
                                    {reviews[ticket.id] === null && (
                                        <div style={{ marginTop: 16, borderTop: '1px solid #2a2a2a', paddingTop: 16 }}>
                                            <p style={{ fontSize: 13, color: '#aaa', marginBottom: 10 }}>Évaluer l'intervention</p>
                                            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <button key={star} type="button"
                                                        onClick={() => setReviewForm(f => ({ ...f, [ticket.id]: { ...f[ticket.id], note: star } }))}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: (reviewForm[ticket.id]?.note || 0) >= star ? '#f59e0b' : '#444' }}
                                                    >★</button>
                                                ))}
                                            </div>
                                            <textarea
                                                placeholder="Commentaire (optionnel)..."
                                                value={reviewForm[ticket.id]?.commentaire || ''}
                                                onChange={e => setReviewForm(f => ({ ...f, [ticket.id]: { ...f[ticket.id], commentaire: e.target.value } }))}
                                                className="textarea"
                                                style={{ minHeight: 60, marginBottom: 10 }}
                                            />
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => submitReview(ticket.id, false)}>Valider</button>
                                                <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => submitReview(ticket.id, true)}>Signaler</button>
                                            </div>
                                        </div>
                                    )}

                                    {reviews[ticket.id] && (
                                        <div style={{ marginTop: 12, borderTop: '1px solid #2a2a2a', paddingTop: 12 }}>
                                            <p style={{ fontSize: 12, color: '#4caf50', margin: 0 }}>
                                                ✓ Avis soumis — {reviews[ticket.id].note}/5 étoiles
                                                {reviews[ticket.id].signalement && <span style={{ color: '#f59e0b', marginLeft: 8 }}>⚠️ Signalement en cours</span>}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </div>
            {/* Modal validation ticket */}
            {validateModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12, padding: 28, maxWidth: 400, width: '100%', textAlign: 'center' }}>
                        <h3 style={{ margin: '0 0 12px', color: '#fff', fontSize: 17 }}>Valider l'intervention</h3>
                        <p style={{ fontSize: 13, color: '#999', marginBottom: 20, lineHeight: 1.6 }}>
                            {validateModal.signalement
                                ? "⚠️ Vous êtes sur le point de signaler un problème. L'équipe Solvya examinera votre dossier."
                                : "En validant, vous confirmez que l'intervention s'est bien déroulée et le paiement sera finalisé."
                            }
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                            <button className="btn" onClick={() => setValidateModal(null)}>Annuler</button>
                            <button
                                className={validateModal.signalement ? "btn btn-danger" : "btn btn-primary"}
                                onClick={confirmValidation}
                            >
                                {validateModal.signalement ? 'Confirmer le signalement' : 'Confirmer la validation'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal fin de session client */}
            {endModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', textAlign: 'center' }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                        <h3 style={{ margin: '0 0 8px', color: '#fff', fontSize: 20 }}>Intervention terminée</h3>
                        <p className="muted" style={{ marginBottom: 20 }}>{endModal.duration} min · {endModal.type === 'express' ? 'Fix Express' : 'À la demande'}</p>

                        <div style={{ fontSize: 42, fontWeight: 800, color: '#fff', marginBottom: 4 }}>
                            {endModal.total.toFixed(2)} €
                        </div>
                        <p className="muted" style={{ fontSize: 13, marginBottom: 24 }}>Montant débité de votre compte</p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => {
                                setEndModal(null);
                                setExpandedTicket(endModal.ticketId);
                            }}>
                                ⭐ Donner un avis
                            </button>
                            <button className="btn btn-danger" style={{ width: '100%', fontSize: 13 }} onClick={() => {
                                setEndModal(null);
                                onNavigate("contact");
                            }}>
                                ⚠️ Signaler un problème
                            </button>
                            <button className="btn" style={{ width: '100%', fontSize: 13 }} onClick={() => setEndModal(null)}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal confirmation ticket */}
            {confirmModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12, padding: 28, maxWidth: 440, width: '100%' }}>
                        <h3 style={{ margin: '0 0 12px', color: '#fff', fontSize: 17 }}>Confirmer la soumission</h3>

                        {ticketType === 'express' ? (
                            <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', marginBottom: 16, fontSize: 13, color: '#4ade80', lineHeight: 1.6 }}>
                                ⚡ <strong>Fix Express — 29 €</strong> pour 15 minutes max. Votre ticket sera traité en priorité. Au-delà de 15 min : 0,75€/min.
                            </div>
                        ) : (
                            <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 16, fontSize: 13, color: '#fbbf24', lineHeight: 1.6 }}>
                                ⚠️ <strong>Forfait minimum de 20 €</strong> (10 min incluses), puis 0,75€/min au-delà.
                            </div>
                        )}

                        <div style={{ fontSize: 13, color: '#999', marginBottom: 16, lineHeight: 1.6 }}>
                            <p style={{ margin: '0 0 8px' }}>⏱️ <strong>Temps d'attente estimé :</strong> {ticketType === 'express' ? 'prioritaire — prise en charge rapide.' : 'variable selon disponibilité des techniciens.'}</p>
                            <p style={{ margin: 0 }}>⚠️ En dehors des heures de forte activité, un technicien pourrait ne pas être disponible immédiatement.</p>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button className="btn" onClick={() => setConfirmModal(false)}>Annuler</button>
                            <button
                                className="btn btn-primary"
                                onClick={createTicket}
                                disabled={loading}
                                style={ticketType === 'express' ? { background: 'rgba(74,222,128,0.18)', borderColor: 'rgba(74,222,128,0.4)', color: '#4ade80' } : {}}
                            >
                                {loading ? 'Envoi...' : ticketType === 'express' ? 'Confirmer — Fix Express 29 €' : 'Confirmer — 20 € minimum'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal annulation ticket */}
            {cancelModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12, padding: 28, maxWidth: 440, width: '100%' }}>
                        <h3 style={{ margin: '0 0 12px', color: '#fff', fontSize: 17 }}>Annuler le ticket</h3>
                        <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', marginBottom: 16, fontSize: 13, color: '#f87171', lineHeight: 1.6 }}>
                            ⚠️ <strong>Même après annulation</strong>, le forfait minimum de 20 € sera prélevé si un technicien a déjà été assigné.
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button className="btn" onClick={() => setCancelModal(null)}>Retour</button>
                            <button className="btn btn-danger" onClick={() => cancelTicket(cancelModal)}>
                                Confirmer l'annulation
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}